/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import { Clock, ChevronDown, ChevronUp } from "lucide-react";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

/**
 * HistoryBlock — read-only audit trail for an entity.
 *
 * Reads from the unified `events` collection (Phase 2 audit refactor).
 * Each event is rendered as a single line: `<actor> <verb> <details>
 * <time>`.  Newest first.
 *
 * Props
 * -----
 *   entity        : { id, kind, ... }
 *   endpoint      : optional override for the list endpoint (default:
 *                   "/{kind}s/{id}/events").
 *   maxItems      : how many events to render at once (default 50).
 *   testIdPrefix  : "history-block" (default)
 */
export default function HistoryBlock({
  entity,
  endpoint,
  maxItems = 50,
  testIdPrefix = "history-block",
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const listUrl = endpoint || `/${entity.kind}s/${entity.id}/events`;

  const reload = useCallback(async () => {
    if (!entity?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(listUrl, { params: { limit: maxItems } });
      const items = Array.isArray(data) ? data : (data?.events || []);
      setEvents(items);
    } catch (_e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [listUrl, entity?.id, maxItems]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <section
      data-testid={`${testIdPrefix}-root`}
      className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4"
    >
      <h3 className="mb-3 text-sm font-medium text-slate-300">
        Activity {events.length > 0 && <span className="text-slate-500">({events.length})</span>}
      </h3>

      {loading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : events.length === 0 ? (
        <p
          data-testid={`${testIdPrefix}-empty`}
          className="text-xs text-slate-500"
        >No activity yet.</p>
      ) : (
        <ol className="space-y-2" data-testid={`${testIdPrefix}-list`}>
          {events.map((ev) => {
            const isOpen = expandedId === ev.id;
            const hasDetails = ev.payload && Object.keys(ev.payload).length > 0;
            return (
              <li
                key={ev.id}
                data-testid={`${testIdPrefix}-item-${ev.id}`}
                className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-3"
              >
                <button
                  onClick={() => hasDetails && setExpandedId(isOpen ? null : ev.id)}
                  className={cn(
                    "flex w-full items-start justify-between gap-3 text-left",
                    hasDetails ? "cursor-pointer" : "cursor-default",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-200">
                      <span className="font-medium">{ev.actor_name || ev.actor_id || "System"}</span>
                      <span className="text-slate-400"> {ev.verb || ev.kind}</span>
                      {ev.summary && <span className="text-slate-300"> · {ev.summary}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      <Clock className="mr-1 inline h-3 w-3" />
                      {ev.created_at_display || ev.at_display || ""}
                    </p>
                  </div>
                  {hasDetails && (
                    isOpen ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                </button>
                {isOpen && hasDetails && (
                  <pre
                    data-testid={`${testIdPrefix}-payload-${ev.id}`}
                    className="mt-2 overflow-x-auto rounded-lg bg-slate-950/80 p-2 text-[11px] text-slate-400"
                  >{JSON.stringify(ev.payload, null, 2)}</pre>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
